/**
 * useEmployeeImport Hook
 * Custom hook for importing employees from Excel file
 * Supports preview + mapping flow for unresolved terms
 */

import { useState, useCallback } from 'react';
import employeeService, {
  ImportPreviewResult,
  EmployeeImportMappings,
} from '../services/employeeService';
import { useSnackbar } from 'notistack';

interface ImportResult {
  success: number;
  errors: Array<{ line: number; error: string }>;
  warnings?: Array<{ line: number; warning: string }>;
}

interface UseEmployeeImportResult {
  importing: boolean;
  previewing: boolean;
  importResult: ImportResult | null;
  preview: ImportPreviewResult | null;
  mappings: Record<string, Record<string, string>>;
  importEmployees: (file: File) => Promise<ImportResult | null>;
  previewImport: (file: File) => Promise<ImportPreviewResult | null>;
  confirmImport: (file: File) => Promise<ImportResult | null>;
  setMappingValue: (column: string, term: string, value: string) => void;
  allRequiredMapped: () => boolean;
  downloadTemplate: () => Promise<void>;
  clearResult: () => void;
  clearPreview: () => void;
}

export const useEmployeeImport = (): UseEmployeeImportResult => {
  const { enqueueSnackbar } = useSnackbar();

  const [importing, setImporting] = useState<boolean>(false);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [mappings, setMappings] = useState<Record<string, Record<string, string>>>({});

  const validateFile = useCallback((file: File): boolean => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      enqueueSnackbar('Tipo de arquivo inválido. Use arquivos .xls ou .xlsx', { variant: 'error' });
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      enqueueSnackbar('Arquivo muito grande. Tamanho máximo: 5MB', { variant: 'error' });
      return false;
    }

    return true;
  }, [enqueueSnackbar]);

  const previewImport = useCallback(async (file: File): Promise<ImportPreviewResult | null> => {
    if (!validateFile(file)) return null;

    try {
      setPreviewing(true);
      const result = await employeeService.previewImport(file);
      setPreview(result);

      if (result.unresolvedTerms.length > 0) {
        const initialMappings: Record<string, Record<string, string>> = {};
        for (const term of result.unresolvedTerms) {
          if (!initialMappings[term.column]) initialMappings[term.column] = {};
          initialMappings[term.column][term.term] = '';
        }
        setMappings(initialMappings);
      }

      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Erro ao processar arquivo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setPreviewing(false);
    }
  }, [enqueueSnackbar, validateFile]);

  const showImportSummary = useCallback((result: ImportResult) => {
    const warningCount = result.warnings?.length ?? 0;
    if (result.errors.length === 0 && warningCount === 0) {
      enqueueSnackbar(`${result.success} funcionário(s) importado(s) com sucesso!`, { variant: 'success' });
    } else if (result.errors.length === 0 && warningCount > 0) {
      enqueueSnackbar(`${result.success} importado(s) com ${warningCount} aviso(s)`, { variant: 'info' });
    } else {
      enqueueSnackbar(
        `${result.success} importado(s), ${result.errors.length} erro(s)${warningCount > 0 ? `, ${warningCount} aviso(s)` : ''}`,
        { variant: 'warning' }
      );
    }
  }, [enqueueSnackbar]);

  const importEmployees = useCallback(async (file: File): Promise<ImportResult | null> => {
    if (!validateFile(file)) return null;

    try {
      setImporting(true);
      const result = await employeeService.importEmployees(file);
      setImportResult(result);
      showImportSummary(result);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao importar funcionários';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setImporting(false);
    }
  }, [enqueueSnackbar, validateFile, showImportSummary]);

  const confirmImport = useCallback(async (file: File): Promise<ImportResult | null> => {
    try {
      setImporting(true);

      // Build typed mappings from the string-based UI mappings
      const importMappings: EmployeeImportMappings = {};

      if (mappings['cargo_nome'] && Object.keys(mappings['cargo_nome']).length > 0) {
        const cargoMappings: Record<string, number> = {};
        for (const [term, value] of Object.entries(mappings['cargo_nome'])) {
          if (value) cargoMappings[term] = Number(value);
        }
        if (Object.keys(cargoMappings).length > 0) importMappings.cargo_nome = cargoMappings;
      }

      if (mappings['genero'] && Object.keys(mappings['genero']).length > 0) {
        const generoMappings: Record<string, string> = {};
        for (const [term, value] of Object.entries(mappings['genero'])) {
          if (value) generoMappings[term] = value;
        }
        if (Object.keys(generoMappings).length > 0) importMappings.genero = generoMappings;
      }

      if (mappings['estado_civil'] && Object.keys(mappings['estado_civil']).length > 0) {
        const ecMappings: Record<string, string> = {};
        for (const [term, value] of Object.entries(mappings['estado_civil'])) {
          if (value) ecMappings[term] = value;
        }
        if (Object.keys(ecMappings).length > 0) importMappings.estado_civil = ecMappings;
      }

      const result = await employeeService.importEmployees(file, {
        mappings: Object.keys(importMappings).length > 0 ? importMappings : undefined,
      });

      setImportResult(result);
      showImportSummary(result);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao importar funcionários';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setImporting(false);
    }
  }, [enqueueSnackbar, mappings, showImportSummary]);

  const setMappingValue = useCallback((column: string, term: string, value: string) => {
    setMappings(prev => ({
      ...prev,
      [column]: { ...prev[column], [term]: value },
    }));
  }, []);

  const allRequiredMapped = useCallback((): boolean => {
    if (!preview) return false;
    return preview.unresolvedTerms
      .filter(t => t.required)
      .every(t => mappings[t.column]?.[t.term]);
  }, [preview, mappings]);

  const downloadTemplate = useCallback(async () => {
    try {
      const blob = await employeeService.downloadTemplate();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-importacao-funcionarios.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Template baixado com sucesso', { variant: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao baixar template';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const clearResult = useCallback(() => {
    setImportResult(null);
  }, []);

  const clearPreview = useCallback(() => {
    setPreview(null);
    setMappings({});
  }, []);

  return {
    importing,
    previewing,
    importResult,
    preview,
    mappings,
    importEmployees,
    previewImport,
    confirmImport,
    setMappingValue,
    allRequiredMapped,
    downloadTemplate,
    clearResult,
    clearPreview,
  };
};
